// ============================================
// CHARGEMENTS DE DOSSIERS HORS LISTE
// ============================================
// Le tableau de bord, l'auto-assignation, l'assistant et les statistiques
// ont leurs propres requêtes : ils ne dépendent plus de la page affichée
// dans la liste. Le serveur plafonne une page à 100 dossiers : les
// chargements « complets » parcourent les pages, avec une borne de sécurité.

import { useEffect, useState } from 'react';
import { AdminApi } from '../../services/api';
import { TicketStatus, type Ticket, type TicketFilters } from '../../types';
import { LIMITE_MAX } from './listeDossiers';

/** 20 pages de 100 : au-delà, l'écran l'indique. */
export const PAGES_MAX = 20;

export async function chargerTousLesDossiers(
  filtres: TicketFilters = {},
  pagesMax = PAGES_MAX,
): Promise<{ dossiers: Ticket[]; total: number; tronque: boolean }> {
  const dossiers: Ticket[] = [];
  let page = 1;
  let totalPages = 1;
  let total = 0;
  do {
    const r = await AdminApi.getTickets({ ...filtres, page, limit: LIMITE_MAX });
    dossiers.push(...r.data);
    total = r.total;
    totalPages = r.totalPages;
    page++;
  } while (page <= totalPages && page <= pagesMax);
  return { dossiers, total, tronque: totalPages > pagesMax };
}

/** Un dossier nouveau ou réouvert, sans agent. */
export const estNonAssigne = (t: Ticket): boolean =>
  !t.assignedToId && (t.status === TicketStatus.OPEN || t.status === TicketStatus.REOPENED);

/** Pages lues par statut pour les dossiers sans agent (10 × 100 dossiers). */
export const PAGES_MAX_NON_ASSIGNES = 10;

/**
 * Dossiers nouveaux ou réouverts sans agent. Le contrat n'a pas de filtre
 * « non assigné » : on lit les statuts OPEN et REOPENED et on garde ceux
 * sans agent. Lecture coûteuse : l'écran la limite (voir DELAI_NON_ASSIGNES_MS).
 * `tronque` : au-delà de la borne, des dossiers n'ont pas été lus ; le compte
 * affiché est alors un minimum.
 */
export async function chargerNonAssignes(): Promise<{ dossiers: Ticket[]; tronque: boolean }> {
  const [ouverts, reouverts] = await Promise.all([
    chargerTousLesDossiers({ status: TicketStatus.OPEN, sortBy: 'createdAt', sortOrder: 'asc' }, PAGES_MAX_NON_ASSIGNES),
    chargerTousLesDossiers({ status: TicketStatus.REOPENED, sortBy: 'createdAt', sortOrder: 'asc' }, PAGES_MAX_NON_ASSIGNES),
  ]);
  return {
    dossiers: [...ouverts.dossiers, ...reouverts.dossiers].filter(estNonAssigne),
    tronque: ouverts.tronque || reouverts.tronque,
  };
}

/** Nombre affiché : « 1 000+ » quand la lecture a été tronquée (c'est un minimum). */
export function libelleNombre(n: number, tronque: boolean): string {
  const texte = n.toLocaleString('fr-FR').replace(/\s/g, ' ');
  return tronque ? `${texte}+` : texte;
}

/** Les derniers dossiers actifs reçus, pour le tableau de bord. */
export async function chargerDerniersDossiers(nombre = 5): Promise<Ticket[]> {
  const r = await AdminApi.getTickets({
    page: 1,
    limit: nombre,
    excludeStatus: [TicketStatus.RESOLVED, TicketStatus.CLOSED],
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  return r.data;
}

/**
 * Charge tous les dossiers correspondant aux filtres quand `actif` est vrai
 * (assistant ouvert, écran de statistiques affiché). `version` relance le
 * chargement : ne pas y brancher le compteur des événements temps réel
 * (jusqu'à 2 000 dossiers relus à chaque changement).
 */
export function useTousLesDossiers(actif: boolean, filtres: TicketFilters, version = 0): { dossiers: Ticket[]; chargement: boolean; tronque: boolean } {
  const [etat, setEtat] = useState<{ dossiers: Ticket[]; chargement: boolean; tronque: boolean }>({ dossiers: [], chargement: false, tronque: false });
  const cle = JSON.stringify(filtres);
  useEffect(() => {
    if (!actif) return;
    let vivant = true;
    setEtat(e => ({ ...e, chargement: true }));
    chargerTousLesDossiers(JSON.parse(cle) as TicketFilters)
      .then(r => { if (vivant) setEtat({ dossiers: r.dossiers, chargement: false, tronque: r.tronque }); })
      .catch(err => {
        console.error('Chargement des dossiers :', err);
        if (vivant) setEtat(e => ({ ...e, chargement: false }));
      });
    return () => { vivant = false; };
  }, [actif, cle, version]);
  return etat;
}
